import { RightOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import caregiversNoAi from 'assets/caregivers-no-ai.png';
import { Page } from 'components/layout/page';
import PageTopBar from 'components/navigation/PageTopBar';
import PostsList from 'components/PostsList';
import { Skeleton } from 'components/ui/skeleton';
import dayjs from 'dayjs';
import useReadProgress from 'hooks/useReadProgress';
import parse from 'html-react-parser';
import posthog from 'posthog-js';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ApiContentManager from 'services/api/ApiContentManager';
import { routes } from 'services/RouteService';
import ScrollUtil from 'utils/ScrollUtil';

const containerClassName = 'min-h-0 grow overflow-y-auto bg-background [&_h2]:text-3xl';
const textContainerClassName = 'mx-auto max-w-content px-4 pt-2';

export default function ArticlePage() {
  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();

  const urlParams = useParams();

  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    run: loadPost,
    data: post,
    loading: isLoadingPost,
  } = useRequest(() => ApiContentManager.getPost(urlParams.index as string), {
    manual: true,
    cacheKey: `content.post.${urlParams.index}`,
    cacheTime: 1000 * 60 * 60, // 60 minutes
  });

  useEffect(() => {
    ScrollUtil.scrollToTop(scrollRef.current);
    loadPost();
  }, [urlParams.index]);

  // the post we have is the one requested (a cached post stays displayed while
  // it is revalidated, so this does not flicker on re-opening)
  const hasRequestedPost = post?.id?.toString() === urlParams.index;

  // how far and how long this post is read, reported to the backend
  useReadProgress(scrollRef, post?.id?.toString(), hasRequestedPost);

  const replaceLinks = (html: string) => {
    // replace <a> tags in the content with <Link> tags to navigate with react router
    return parse(html, {
      replace: (node: any) => {
        if (node.name === 'a' && node.attribs?.href) {
          const { href, ...restAttribs } = node.attribs;
          const text = node.children?.[0]?.data || '';
          // Replace <a> with <Link> while preserving all other attributes
          return (
            <Link
              to={href}
              {...restAttribs}
              onClick={() => {
                if (href.startsWith('/')) {
                  posthog.capture('info.article.link_to_other_section_clicked', {
                    article_id: post?.id,
                    article_title: post?.title,
                    link_url: href,
                  });
                }
              }}
            >
              {text}
            </Link>
          );
        }
      },
    });
  };

  function back() {
    posthog.capture('info.post_closed');
    navigate(-1);
  }

  const openChat = () => {
    navigate(routes.messagingHome.route);
    posthog.capture('info.article.chat_call_to_action_clicked', {
      article_id: post?.id,
      article_title: post?.title,
    });
  };

  return (
    <Page className="items-stretch">
      <PageTopBar title={t('menu.info')} backButton onBackButtonClicked={() => back()} />
      {isLoadingPost && !hasRequestedPost ? (
        <div className={containerClassName} ref={scrollRef}>
          <div className={`${textContainerClassName} flex flex-col gap-3`}>
            <Skeleton className="mb-3 h-6 w-2/5" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="mb-3 h-4 w-3/5" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="mb-3 h-4 w-3/5" />
            <Skeleton className="mb-3 h-6 w-2/5" />
            <Skeleton className="h-16 w-full" />
          </div>
        </div>
      ) : (
        <div className={containerClassName} ref={scrollRef} data-testid="article-scroll">
          {post?.cover_image_url && (
            <div className="h-48 w-full overflow-hidden">
              <img src={post?.cover_image_url} alt={post?.title} className="size-full object-cover" />
            </div>
          )}
          <div className={textContainerClassName}>
            <h2 className="py-5">{post?.title}</h2>
            <div
              // Links: `text-link` is the same teal as the body text, so it would be
              // invisible here; the accent that is readable as text sets them apart.
              className="mb-4 text-lg [&_a]:text-link-hover [&_a]:underline [&_a:hover]:text-foreground [&_a:active]:text-foreground [&_h3]:mt-8 [&_h3]:mb-2 [&_h4]:mt-6 [&_h4]:mb-1 [&_iframe]:max-w-full [&_iframe]:border-0 [&_img]:h-auto [&_img]:max-w-full"
            >
              {replaceLinks(post?.content ?? '')}
            </div>
            <p className="mb-12 text-subtle-foreground">
              {t('infoPage.article.writtenBy', {
                author: post?.author.display_name,
                date: dayjs(post?.created_at).format('LL'),
              })}
            </p>
            {(post?.related_posts?.length ?? 0) > 0 && (
              <>
                <h3>{t('infoPage.article.relatedPosts')}</h3>
                <PostsList
                  posts={post?.related_posts ?? []}
                  posthogOnArticleOpenExtras={{ opened_on_page: 'article', referring_article_id: post?.id?.toString() }}
                />
              </>
            )}
            <div
              className="bg-card mx-4 my-8 flex items-center gap-4 rounded-lg p-4 shadow-sm [&_h4]:m-0 [&_p]:m-0"
              onClick={openChat}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  openChat();
                }
              }}
              role="button"
              tabIndex={0}
            >
              <img src={caregiversNoAi} alt="chat" height={48} width={48} />
              <div>
                <h4 className="text-base font-medium">{t('infoPage.article.chatCallToAction.title')}</h4>
                <p>{t('infoPage.article.chatCallToAction.description')}</p>
              </div>
              <RightOutlined />
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
